/* The Aesthetic Cove — Christmas intro, ivory edition.
   A Warm Ivory invitation card. A champagne hairline frame draws itself
   around the screen while pearl snowflakes fall at three depths: fine dust
   far away, crisp six-armed crystals in the middle, and big soft flakes
   drifting past the lens. Muted fir sprigs slide in at the top corners and
   glass baubles (muted red, champagne, cashmere, pearl, taupe) drop from them
   on antique-gold threads, bounce and sway. The mouse tilts the layers a
   little for depth.
   1. A giant champagne snowflake grows from the centre, arm by arm, behind
      the wordmark, and a ring of champagne dots closes around it.
   2. Antique-gold and ink glitter pours in and gathers into the wordmark
      (sampled from text drawn in Libre Caslon on an offscreen canvas). The
      cursor or a finger pushes it around. Sparkles flash on the settled
      letters.
   3. Santa's sleigh crosses once on a high arc behind the letters, trailing
      glitter.
   4. The counter reaches 100 once the hero's first images have loaded. The
      crisp wordmark lands with a flash: a horizontal light flare, a ring of
      sparkles and champagne snowflakes bursting outward, and the big
      snowflake brightens. A foil shimmer sweeps the letters.
   5. The bottom line fades in and holds, the glitter exhales and the card
      lifts away on a champagne edge of light, revealing the page.

   Timeline, in seconds from the first frame when nothing is skipped:
     Retimed for ad traffic: about 4.7 seconds in all.
     0.0  snow; the frame draws itself (done by 1.3); top line and counter fade in
     0.15 fir sprigs slide in; baubles drop between 0.5 and 1.2
     0.15 the big snowflake grows (to 1.85); its dotted ring closes by about 2.1
     0.0  glitter pours; letters gathered by about 1.6, sparkles begin
     0.35 sleigh takes off; off-screen right at about 2.65
     2.5  the wordmark lands: flare, burst, crisp letters   [the hard cap can pull this earlier]
     2.65 foil shimmer (0.8)
     3.2  bottom line fades in (0.4), holds
     3.75 exhale and lift (1.0)
     4.75 done. Skip, Escape and Enter end it at once, whatever the phase.
   Nobody is held longer than HOLD_MAX seconds from the moment this script runs. */

(() => {
  const el = document.querySelector('[data-christmas-intro]');
  if (!el || el.hasAttribute('data-skip')) return;

  const bootAt = performance.now();
  const root = document.documentElement;
  const stage = el.querySelector('[data-intro-stage]') || el;
  const edge = el.querySelector('[data-intro-edge]');
  const canvas = el.querySelector('[data-intro-canvas]');
  const ctx = canvas && canvas.getContext('2d');
  const countEl = el.querySelector('[data-intro-count]');
  const fades = el.querySelectorAll('[data-intro-fade]');
  const lineBottom = el.querySelector('[data-intro-line-bottom]');
  const exitFades = lineBottom ? [...fades, lineBottom] : [...fades];
  const skipBtn = el.querySelector('[data-intro-skip]');
  const sleigh = el.querySelector('[data-intro-sleigh]');
  const rulesH = el.querySelectorAll('[data-intro-rule="h"]');
  const rulesV = el.querySelectorAll('[data-intro-rule="v"]');
  const corners = el.querySelectorAll('[data-intro-corner]');
  const G = window.gsap;

  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  const small = window.innerWidth < 750;
  const TAU = Math.PI * 2;

  /* ---------- pointer ---------- */
  const pointer = { x: -9999, y: -9999, active: false };
  const onMove = (e) => {
    const p = e.touches ? e.touches[0] : e;
    if (!p) return;
    pointer.x = p.clientX * dpr;
    pointer.y = p.clientY * dpr;
    pointer.active = true;
  };

  let done = false;
  let raf = 0;
  let edgeH = 0;
  let flightTween = null;
  let lineTween = null;

  const finish = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    if (flightTween) flightTween.kill();
    if (lineTween) lineTween.kill();
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('touchmove', onMove);
    window.removeEventListener('keydown', onKey);
    root.classList.remove('intro-active');
    try {
      sessionStorage.setItem('tac-intro-seen', '1');
    } catch (e) {
      /* private mode */
    }
    if (window.lenis) window.lenis.start();
    el.remove();
    if (typeof window.themeMotionStart === 'function') window.themeMotionStart();
    window.__introDone = true;
    window.dispatchEvent(new Event('intro:done'));
  };

  function onKey(e) {
    if (e.key === 'Escape' || e.key === 'Enter') toExhale();
  }

  if (!ctx || !G) {
    finish();
    return;
  }
  if (window.lenis) window.lenis.stop();
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('touchmove', onMove, { passive: true });

  const TARGET_COUNT = small ? 1500 : 3000;
  const DUST_COUNT = small ? 90 : 170; // far snow: fine dots
  const CRYSTAL_COUNT = small ? 24 : 46; // middle snow: crisp six-armed crystals
  const NEAR_COUNT = small ? 3 : 6; // big soft flakes drifting past the lens
  const TRAIL_MAX = small ? 160 : 320;
  const STAR_COUNT = small ? 34 : 64;
  const BOKEH_COUNT = small ? 5 : 10;
  const LETTER_SPARKS = small ? 9 : 16;
  const BURST_COUNT = small ? 20 : 34;

  /* ---------- timing (seconds) ---------- */
  const HOLD_MAX = 5.4; // hard cap on screen, total, from the moment this script runs
  const MIN_TIME = 2.5; // earliest the wordmark may land after the first frame
  const RESOLVE_FADE = 0.45; // crisp wordmark fade-in
  const SHIMMER_AT = 0.15; // after the landing
  const SHIMMER = 0.8; // one diagonal sweep
  const LINE_AT = SHIMMER_AT + SHIMMER - 0.25; // bottom line starts as the band leaves the last letters
  const LINE_FADE = 0.4;
  const EXIT_AT = LINE_AT + 0.55; // bottom line seen for about a second, then the exhale
  const EXIT = 1.0; // exhale plus lift
  const TAIL = EXIT_AT + EXIT; // everything after the landing (about 3.4s)
  const SETTLE_AT = 1.6; // letters are gathered; sparkles may start
  const SLEIGH_DELAY = 0.35;
  const SLEIGH_TIME = 2.3;
  const GROW_AT = 0.15; // the big snowflake starts growing
  const GROW = 1.7;

  /* ---------- palette ----------
     Canvas cannot read the theme's colour tokens, so these mirror them:
     champagne #C3A26E (--gold), antique champagne #9F8153 (--gold-deep),
     light champagne #DEC9A5 (--gold-light), ink #4A3C33, bronze #6E5B4E,
     taupe #8B7564, cashmere #D9C8B4, snow #FAF8F3, pine #5B7656. Everything
     is drawn on Warm Ivory #F2EBDD, so the glitter runs darker than the
     page's gold and the snow carries a cashmere edge to stay visible. */
  const GOLD = ['#C3A26E', '#B08E58', '#9F8153', '#9F8153', '#D4B98A', '#8A6B3E', '#DEC9A5'];
  const PALE = ['#DEC9A5', '#C3A26E', '#FFF6EC'];
  const SNOW = ['#FAF8F3', '#FFFDF8', '#FBF6EC'];
  const INK = ['#4A3C33', '#4A3C33', '#6E5B4E', '#8B7564'];
  const SNOW_EDGE = 'rgba(169,147,124,0.55)'; // cashmere, deepened a touch, the halo under every snow dot
  const TEXT_INK = '#4A3C33';
  const GOLD_INK = '#9F8153';
  const STAR_INK = '#C3A26E';
  const SPARK_INK = '#FFFCF5';
  const MED_INK = '#BE9C64';
  const FAMILY = "'Libre Caslon Display', 'Libre Caslon Text', Georgia, serif";
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  const smooth = (k) => k * k * (3 - 2 * k);
  const clamp01 = (k) => (k < 0 ? 0 : k > 1 ? 1 : k);
  const easeOut = (k) => 1 - Math.pow(1 - k, 3);
  const mulberry32 = (a) => () => {
    a = (a + 0x6d2b79f5) | 0;
    let q = Math.imul(a ^ (a >>> 15), 1 | a);
    q = (q + Math.imul(q ^ (q >>> 7), 61 | q)) ^ q;
    return ((q ^ (q >>> 14)) >>> 0) / 4294967296;
  };

  // Particle kinds: 0 glitter fleck, 1 soft snow dot, 2 four-point sparkle.
  const GLITTER = 0;
  const FLAKE = 1;
  const SPARKLE = 2;

  let W = 0;
  let H = 0;
  // Viewport size in CSS px, read once per resize: reading window.innerWidth
  // inside the frame loop forces a layout every frame.
  let vpW = window.innerWidth;
  let vpH = window.innerHeight;
  const resize = () => {
    vpW = window.innerWidth;
    vpH = window.innerHeight;
    W = canvas.width = Math.round(vpW * dpr);
    H = canvas.height = Math.round(vpH * dpr);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  };
  resize();

  /* ---------- zero-size guard ----------
     In a hidden pane or a 0×0 viewport the canvas has no size and getImageData
     throws. Wait up to two seconds for a size, then give up and show the page. */
  const whenSized = (cb) => {
    if (W > 0 && H > 0) {
      cb();
      return;
    }
    let settled = false;
    let ro = null;
    const deadline = performance.now() + 2000;
    const settle = (ok) => {
      if (settled) return;
      settled = true;
      if (ro) ro.disconnect();
      if (ok) cb();
      else finish();
    };
    const check = () => {
      if (settled || done) return;
      resize();
      if (W > 0 && H > 0) settle(true);
      else if (performance.now() > deadline) settle(false);
      else requestAnimationFrame(check);
    };
    if (window.ResizeObserver) {
      ro = new ResizeObserver(check);
      ro.observe(el);
    }
    requestAnimationFrame(check);
    // requestAnimationFrame pauses in background tabs; a timer still fires.
    setTimeout(() => {
      resize();
      settle(W > 0 && H > 0);
    }, 2200);
  };

  /* ---------- sprites, built once ---------- */
  function makeDisc(size, rgb) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0, `rgba(${rgb},1)`);
    grad.addColorStop(0.3, `rgba(${rgb},0.8)`);
    grad.addColorStop(0.7, `rgba(${rgb},0.22)`);
    grad.addColorStop(1, `rgba(${rgb},0)`);
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    return c;
  }
  const DISC_GOLD = makeDisc(256, '195,162,110');
  const DISC_CHAMPAGNE = makeDisc(256, '222,201,165');
  const DISC_WHITE = makeDisc(256, '255,246,236');
  const DISC_PINE = makeDisc(256, '91,118,86');
  const GLOW = DISC_GOLD;

  // A horizontal anamorphic light streak for the moment the wordmark lands.
  const FLARE = (() => {
    const c = document.createElement('canvas');
    c.width = 512;
    c.height = 64;
    const g = c.getContext('2d');
    g.scale(1, 64 / 512);
    const grad = g.createRadialGradient(256, 256, 0, 256, 256, 256);
    grad.addColorStop(0, 'rgba(255,252,246,1)');
    grad.addColorStop(0.08, 'rgba(255,246,236,0.9)');
    grad.addColorStop(0.3, 'rgba(222,201,165,0.35)');
    grad.addColorStop(1, 'rgba(222,201,165,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 512, 512);
    return c;
  })();

  /* Six-armed snowflake crystals. Each design is one arm along +u (unit
     length), as segments [u1, v1, u2, v2]; branches leave at 60 degrees. */
  const CRYSTAL_ARMS = [
    [[0.1, 0, 1, 0], [0.3, 0, 0.43, 0.22], [0.3, 0, 0.43, -0.22], [0.52, 0, 0.63, 0.19], [0.52, 0, 0.63, -0.19], [0.72, 0, 0.79, 0.12], [0.72, 0, 0.79, -0.12]],
    [[0, 0, 0.86, 0], [0.56, 0, 0.66, 0.17], [0.56, 0, 0.66, -0.17], [0.86, 0, 0.93, 0.07], [0.93, 0.07, 1, 0], [1, 0, 0.93, -0.07], [0.93, -0.07, 0.86, 0]],
    [[0, 0, 1, 0], [0.6, 0, 0.8, 0.17], [0.6, 0, 0.8, -0.17], [0.3, 0, 0.38, 0.12], [0.3, 0, 0.38, -0.12]],
    [[0.08, 0, 1, 0], [0.22, 0, 0.36, 0.24], [0.22, 0, 0.36, -0.24], [0.38, 0, 0.5, 0.2], [0.38, 0, 0.5, -0.2], [0.54, 0, 0.64, 0.16], [0.54, 0, 0.64, -0.16], [0.7, 0, 0.77, 0.11], [0.7, 0, 0.77, -0.11], [0.84, 0, 0.88, 0.06], [0.84, 0, 0.88, -0.06]],
  ];
  const CRYSTAL_HEX = [0.1, 0.3, 0, 0]; // inner hexagon radius per design, 0 for none

  // `glow` is the halo colour behind the lines (pearl crystals carry a
  // cashmere halo so they read on ivory); pass null for none.
  function makeCrystal(size, design, colour, glow, lw) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const R = size * (glow ? 0.4 : 0.46);
    g.translate(size / 2, size / 2);
    g.strokeStyle = colour;
    g.lineWidth = Math.max(1, R * lw);
    g.lineCap = 'round';
    g.lineJoin = 'round';
    if (glow) {
      g.shadowColor = glow;
      g.shadowBlur = R * 0.22;
    }
    g.beginPath();
    for (let k = 0; k < 6; k++) {
      const a = (k * TAU) / 6 - Math.PI / 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      for (const [u1, v1, u2, v2] of CRYSTAL_ARMS[design]) {
        g.moveTo((u1 * ca - v1 * sa) * R, (u1 * sa + v1 * ca) * R);
        g.lineTo((u2 * ca - v2 * sa) * R, (u2 * sa + v2 * ca) * R);
      }
    }
    const hr = CRYSTAL_HEX[design];
    if (hr) {
      for (let k = 0; k <= 6; k++) {
        const a = (k * TAU) / 6 - Math.PI / 2;
        const x = Math.cos(a) * hr * R;
        const y = Math.sin(a) * hr * R;
        if (k) g.lineTo(x, y);
        else g.moveTo(x, y);
      }
    }
    g.stroke();
    return c;
  }
  const CRYSTAL_PX = Math.round(64 * dpr);
  // Pearl crystals: snow-white lines inside a taupe halo, so they sit on ivory like frost on card.
  const SPR_PEARL = [0, 1, 2, 3].map((d) => makeCrystal(CRYSTAL_PX, d, '#FAF8F3', 'rgba(139,117,100,0.95)', 0.075));
  const SPR_GOLD = [0, 1, 2, 3].map((d) => makeCrystal(CRYSTAL_PX, d, '#B8955C', 'rgba(195,162,110,0.9)', 0.08));
  // Drawn tiny on purpose: scaled up many times, they come out soft, like flakes out of focus.
  // Champagne rather than white: a white blur vanishes on ivory, a warm one reads as a flake passing.
  const SPR_SOFT = [0, 1, 2, 3].map((d) => makeCrystal(30, d, '#B08E58', null, 0.12));
  const SPR_ENGRAVE = makeCrystal(CRYSTAL_PX, 1, '#E6D2A8', null, 0.075);

  /* ---------- glass baubles ---------- */
  // Each colourway is lit from the upper left: highlight, body, shade, deep shade.
  const BAUBLE_STYLES = {
    red: ['#D9857C', '#A94A43', '#7E3631', '#4A1F1C'],
    champagne: ['#F3E6C8', '#C3A26E', '#8E6F42', '#4A3414'],
    cashmere: ['#FBF6EE', '#D9C8B4', '#A98F74', '#5E4A3A'],
    pearl: ['#FFFDF8', '#F6F1E7', '#CDBDA8', '#7C6A5A'],
    taupe: ['#C9B5A4', '#8B7564', '#5C4D43', '#3B2E26'],
  };
  // Returns the sprite and where its sphere sits relative to the loop at the top.
  function makeBauble(R, style, engraved) {
    const s = BAUBLE_STYLES[style];
    const capH = R * 0.34;
    const loop = R * 0.13;
    const pad = Math.ceil(R * 0.12 + 2);
    const w = Math.ceil(R * 2 + pad * 2);
    const capTop = pad + loop * 2;
    const cy = capTop + capH * 0.75 + R;
    const h = Math.ceil(cy + R + pad);
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d');
    const cx = w / 2;

    // Body: lit from the upper left.
    let gr = g.createRadialGradient(cx - R * 0.35, cy - R * 0.4, R * 0.04, cx, cy, R * 1.02);
    gr.addColorStop(0, s[0]);
    gr.addColorStop(0.32, s[1]);
    gr.addColorStop(0.78, s[2]);
    gr.addColorStop(1, s[3]);
    g.fillStyle = gr;
    g.beginPath();
    g.arc(cx, cy, R, 0, TAU);
    g.fill();

    g.save();
    g.beginPath();
    g.arc(cx, cy, R, 0, TAU);
    g.clip();
    // Warm light bouncing back from the lower right.
    gr = g.createRadialGradient(cx + R * 0.5, cy + R * 0.55, 0, cx + R * 0.5, cy + R * 0.55, R * 0.8);
    gr.addColorStop(0, 'rgba(255,228,176,0.26)');
    gr.addColorStop(1, 'rgba(255,228,176,0)');
    g.fillStyle = gr;
    g.fillRect(cx - R, cy - R, R * 2, R * 2);
    if (engraved) {
      // A laser-engraved champagne snowflake, the shop's own trade.
      const sz = R * 1.12;
      g.globalAlpha = 0.92;
      g.drawImage(SPR_ENGRAVE, cx - sz / 2, cy - sz / 2 + R * 0.04, sz, sz);
      g.globalAlpha = 1;
    } else {
      // A band around the middle: the front half bright, the back half faint.
      // Light champagne on the dark glass, antique champagne on the pale glass.
      g.lineWidth = Math.max(1, R * 0.05);
      g.strokeStyle =
        style === 'champagne' ? 'rgba(255,248,236,0.75)' : style === 'red' || style === 'taupe' ? 'rgba(222,201,165,0.95)' : 'rgba(159,129,83,0.9)';
      g.beginPath();
      g.ellipse(cx, cy + R * 0.1, R, R * 0.24, 0, 0, Math.PI);
      g.stroke();
      g.globalAlpha = 0.28;
      g.beginPath();
      g.ellipse(cx, cy + R * 0.1, R, R * 0.24, 0, Math.PI, TAU);
      g.stroke();
      g.globalAlpha = 1;
    }
    // Specular highlight.
    g.save();
    g.translate(cx - R * 0.4, cy - R * 0.42);
    g.rotate(-0.7);
    g.scale(1, 0.55);
    gr = g.createRadialGradient(0, 0, 0, 0, 0, R * 0.32);
    gr.addColorStop(0, 'rgba(255,252,246,0.92)');
    gr.addColorStop(0.4, 'rgba(255,252,246,0.35)');
    gr.addColorStop(1, 'rgba(255,252,246,0)');
    g.fillStyle = gr;
    g.beginPath();
    g.arc(0, 0, R * 0.32, 0, TAU);
    g.fill();
    g.restore();
    // A soft taupe edge down the lower right so pale glass reads against the ivory card.
    g.strokeStyle = 'rgba(74,60,51,0.28)';
    g.lineWidth = Math.max(1, R * 0.06);
    g.beginPath();
    g.arc(cx, cy, R * 0.97, -Math.PI * 0.15, Math.PI * 0.6);
    g.stroke();
    g.restore();

    // Antique-gold cap with ridges, and the hanging loop.
    const cw = R * 0.46;
    gr = g.createLinearGradient(cx - cw / 2, 0, cx + cw / 2, 0);
    gr.addColorStop(0, '#6B5130');
    gr.addColorStop(0.35, '#E6D2A8');
    gr.addColorStop(0.65, '#A98656');
    gr.addColorStop(1, '#4A3414');
    g.fillStyle = gr;
    g.fillRect(cx - cw / 2, capTop, cw, capH);
    g.fillRect(cx - cw * 0.56, capTop + capH * 0.78, cw * 1.12, capH * 0.22);
    g.strokeStyle = 'rgba(74,52,20,0.45)';
    g.lineWidth = Math.max(0.6, R * 0.02);
    g.beginPath();
    for (let i = 1; i < 6; i++) {
      const x = cx - cw / 2 + (cw * i) / 6;
      g.moveTo(x, capTop + capH * 0.12);
      g.lineTo(x, capTop + capH * 0.74);
    }
    g.stroke();
    g.strokeStyle = '#9F8153';
    g.lineWidth = Math.max(1, R * 0.05);
    g.beginPath();
    g.arc(cx, pad + loop, loop, 0, TAU);
    g.stroke();

    return { img: c, top: pad, cy: cy - pad, R };
  }

  /* ---------- fir sprigs ----------
     Drawn once, needle by needle, in three layers of muted fir green from
     dark to light, with a little champagne dust and snow on the tips. The
     main stem is a curve in the sprig's own space; baubles hang from points
     along it. */
  const quadAt = (q, s) => {
    const u = 1 - s;
    return [u * u * q[0][0] + 2 * u * s * q[1][0] + s * s * q[2][0], u * u * q[0][1] + 2 * u * s * q[1][1] + s * s * q[2][1]];
  };
  const mainStem = (L) => [
    [0, 0],
    [0.55 * L, -0.07 * L],
    [L, 0.03 * L],
  ];

  function makeBranch(L, seed) {
    const rand = mulberry32(seed);
    const pad = Math.ceil(L * 0.2);
    const w = Math.ceil(L * 1.02 + pad * 2);
    const h = Math.ceil(L * 0.62 + pad * 2);
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d');
    const ox = pad;
    const oy = Math.round(h * 0.42);
    g.translate(ox, oy);
    g.lineCap = 'round';

    const main = mainStem(L);
    const twigs = [{ q: main, n: 1 }];
    [
      [0.18, 1, 0.42],
      [0.34, -1, 0.36],
      [0.52, 1, 0.3],
      [0.7, -1, 0.21],
      [0.84, 1, 0.13],
    ].forEach(([s, side, k]) => {
      const p = quadAt(main, s);
      const q = quadAt(main, Math.min(1, s + 0.01));
      const a = Math.atan2(q[1] - p[1], q[0] - p[0]) + side * 0.62;
      const len = L * k;
      const mid = [p[0] + Math.cos(a - side * 0.18) * len * 0.5, p[1] + Math.sin(a - side * 0.18) * len * 0.5];
      const end = [p[0] + Math.cos(a) * len, p[1] + Math.sin(a) * len];
      twigs.push({ q: [p, mid, end], n: k / 0.45 });
    });

    // Woody stems first (warm bark brown); the needles cover most of them.
    g.strokeStyle = '#5C4D43';
    twigs.forEach(({ q, n }) => {
      g.lineWidth = Math.max(1, (n >= 1 ? 2.4 : 1.6) * dpr);
      g.beginPath();
      g.moveTo(q[0][0], q[0][1]);
      g.quadraticCurveTo(q[1][0], q[1][1], q[2][0], q[2][1]);
      g.stroke();
    });

    const NEEDLE = L * 0.072;
    const layers = [
      { cols: ['#2F4A33', '#374F3A', '#3F5842'], k: 1.0, spread: 1.0, lw: 1.7 },
      { cols: ['#4A6448', '#54704F', '#5B7656'], k: 0.9, spread: 0.82, lw: 1.45 },
      { cols: ['#6E8A68', '#7E9A77', '#8CA883'], k: 0.66, spread: 0.62, lw: 1.1 },
    ];
    const tips = [];
    layers.forEach((ly, li) => {
      const paths = ly.cols.map(() => new Path2D());
      twigs.forEach(({ q, n }) => {
        const len = Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]) + Math.hypot(q[2][0] - q[1][0], q[2][1] - q[1][1]);
        const steps = Math.max(8, Math.round(len / (2.6 * dpr)));
        for (let i = 0; i <= steps; i++) {
          const s = i / steps;
          const p = quadAt(q, s);
          const pa = quadAt(q, Math.max(0, s - 0.01));
          const pb = quadAt(q, Math.min(1, s + 0.01));
          const a = Math.atan2(pb[1] - pa[1], pb[0] - pa[0]);
          const taper = s > 0.8 ? 1 - (s - 0.8) * 2.6 : s < 0.06 ? 0.6 + s * 6.6 : 1;
          for (let side = -1; side <= 1; side += 2) {
            const nl = NEEDLE * Math.min(1, 0.55 + n * 0.45) * ly.k * Math.max(0.3, taper) * (0.78 + rand() * 0.44);
            const an = a + side * (ly.spread + (rand() - 0.5) * 0.3);
            const ex = p[0] + Math.cos(an) * nl;
            const ey = p[1] + Math.sin(an) * nl;
            const path = paths[(rand() * paths.length) | 0];
            path.moveTo(p[0], p[1]);
            path.lineTo(ex, ey);
            if (li === 2 && rand() < 0.05) tips.push([ex, ey]);
          }
        }
      });
      g.lineWidth = ly.lw * dpr;
      paths.forEach((p, i) => {
        g.strokeStyle = ly.cols[i];
        g.stroke(p);
      });
    });
    tips.forEach(([x, y]) => {
      g.fillStyle = rand() < 0.6 ? '#C3A26E' : 'rgba(250,248,243,0.95)';
      g.beginPath();
      g.arc(x, y, (0.7 + rand() * 0.7) * dpr, 0, TAU);
      g.fill();
    });
    return { img: c, ox, oy, L };
  }

  /* ---------- sky: bokeh lights and stars ---------- */
  const bokeh = [];
  const stars = [];
  function makeSky() {
    bokeh.length = 0;
    stars.length = 0;
    for (let i = 0; i < BOKEH_COUNT; i++) {
      const green = i % 4 === 3; // a few fir-green lights among the champagne
      bokeh.push({
        x: Math.random() * W,
        y: Math.random() * H,
        r: (30 + Math.random() * 60) * dpr,
        a: green ? 0.05 + Math.random() * 0.04 : 0.08 + Math.random() * 0.1,
        ax: (10 + Math.random() * 16) * dpr,
        ay: (6 + Math.random() * 12) * dpr,
        sx: 0.06 + Math.random() * 0.08,
        sy: 0.05 + Math.random() * 0.07,
        ph: Math.random() * TAU,
        bs: 0.3 + Math.random() * 0.4,
        img: green ? DISC_PINE : pick([DISC_GOLD, DISC_CHAMPAGNE, DISC_WHITE]),
      });
    }
    for (let i = 0; i < STAR_COUNT; i++) {
      const bright = i < 5;
      stars.push({
        x: Math.random() * W,
        y: Math.random() * H * 0.8,
        r: (bright ? 1.5 + Math.random() * 1 : 0.5 + Math.random() * 0.8) * dpr,
        a: bright ? 0.5 + Math.random() * 0.35 : 0.25 + Math.random() * 0.5,
        sp: 0.7 + Math.random() * 2.2,
        ph: Math.random() * TAU,
        slow: 0.12 + Math.random() * 0.28,
        ph2: Math.random() * TAU,
        bright,
      });
    }
  }

  /* ---------- wordmark drawn offscreen, then sampled ---------- */
  const textCanvas = document.createElement('canvas');
  const tctx = textCanvas.getContext('2d');
  const glowCanvas = document.createElement('canvas');
  const top = (el.dataset.top || 'The').toUpperCase();
  const bottom = el.dataset.bottom || 'Aesthetic Cove';

  function measureSpaced(c, text, spacing) {
    const chars = [...text];
    return chars.reduce((a, ch) => a + c.measureText(ch).width, 0) + spacing * Math.max(0, chars.length - 1);
  }

  function drawSpaced(c, text, x, y, spacing) {
    // Canvas letterSpacing is not universal yet, so place characters by hand.
    const chars = [...text];
    const widths = chars.map((ch) => c.measureText(ch).width);
    const total = widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, chars.length - 1);
    let cx = x - total / 2;
    chars.forEach((ch, i) => {
      c.fillText(ch, cx, y);
      cx += widths[i] + spacing;
    });
    return total;
  }

  function renderWordmark() {
    textCanvas.width = W;
    textCanvas.height = H;
    tctx.clearRect(0, 0, W, H);
    tctx.textBaseline = 'alphabetic';
    tctx.textAlign = 'start';

    // The bottom word is wide: size it to 90% of the viewport on phones, 66% on desktop.
    const maxW = W * (small ? 0.9 : 0.66);
    tctx.font = `400 100px ${FAMILY}`;
    const probe = tctx.measureText(bottom).width || 100;
    const bigSize = Math.max(16 * dpr, Math.min((100 * maxW) / probe, H * 0.22));
    const bottomW = (probe * bigSize) / 100;

    let smallSize = Math.max(10 * dpr, bigSize * 0.17);
    let spacing = smallSize * 0.42;
    tctx.font = `500 ${smallSize}px ${FAMILY}`;
    let topW = measureSpaced(tctx, top, spacing);
    if (topW > maxW) {
      const k = maxW / topW;
      smallSize *= k;
      spacing *= k;
      tctx.font = `500 ${smallSize}px ${FAMILY}`;
      topW = measureSpaced(tctx, top, spacing);
    }

    const cx = W / 2;
    const yb = H / 2 + bigSize * 0.36; // baseline of the big champagne word
    const yt = yb - bigSize * 0.98; // baseline of the small ink line

    tctx.fillStyle = TEXT_INK;
    drawSpaced(tctx, top, cx, yt, spacing);

    // Antique champagne foil: light at the top of the letters, deep through
    // the middle, a little reflected light at the foot. Runs darker than the
    // page's gold so the big word holds its own on ivory.
    const foil = tctx.createLinearGradient(0, yb - bigSize * 0.78, 0, yb + bigSize * 0.05);
    foil.addColorStop(0, '#CDB088');
    foil.addColorStop(0.38, GOLD_INK);
    foil.addColorStop(0.72, '#7A5F3D');
    foil.addColorStop(1, '#B4986A');
    tctx.fillStyle = foil;
    tctx.font = `400 ${bigSize}px ${FAMILY}`;
    tctx.textAlign = 'center';
    tctx.fillText(bottom, cx, yb);
    tctx.textAlign = 'start';

    // A soft halo: the wordmark shrunk to an eighth and scaled back up comes out blurred.
    glowCanvas.width = Math.max(1, Math.round(W / 8));
    glowCanvas.height = Math.max(1, Math.round(H / 8));
    const gctx = glowCanvas.getContext('2d');
    gctx.clearRect(0, 0, glowCanvas.width, glowCanvas.height);
    gctx.drawImage(textCanvas, 0, 0, glowCanvas.width, glowCanvas.height);

    // Only the area around the wordmark needs scanning, and never beyond the canvas.
    const half = Math.max(bottomW, topW) / 2 + bigSize * 0.12;
    const x0 = Math.max(0, Math.floor(cx - half));
    const x1 = Math.min(W, Math.ceil(cx + half));
    const y0 = Math.max(0, Math.floor(yt - smallSize * 1.05));
    const y1 = Math.min(H, Math.ceil(yb + bigSize * 0.3));
    const box = { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
    return { cx, cy: (yt + yb) / 2, yb, bigSize, splitY: yt + smallSize * 0.35, box };
  }

  function sampleTargets(splitY, box) {
    if (!(box.w > 0 && box.h > 0)) return [];
    let data;
    try {
      data = tctx.getImageData(box.x, box.y, box.w, box.h).data;
    } catch (e) {
      return [];
    }
    let step = Math.max(2, Math.round(2.4 * dpr));
    let pts = [];
    for (let tries = 0; tries < 6; tries++) {
      pts = [];
      for (let y = 0; y < box.h; y += step) {
        for (let x = 0; x < box.w; x += step) {
          if (data[(y * box.w + x) * 4 + 3] > 128) pts.push([x + box.x, y + box.y]);
        }
      }
      if (pts.length <= TARGET_COUNT * 1.6) break;
      step += 1;
    }
    // Thin out evenly at random down to the budget.
    for (let i = pts.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [pts[i], pts[j]] = [pts[j], pts[i]];
    }
    return pts.slice(0, TARGET_COUNT).map(([x, y]) => ({ x, y, upper: y < splitY }));
  }

  /* ---------- foil shimmer: a light band masked to a copy of the wordmark ---------- */
  const shineCanvas = document.createElement('canvas');
  const sctx = shineCanvas.getContext('2d');
  const shine = { grad: null, bw: 0 };
  function prepShine(box) {
    shineCanvas.width = Math.max(1, box.w);
    shineCanvas.height = Math.max(1, box.h);
    shine.bw = Math.max(50 * dpr, Math.min(box.h * 0.45, 140 * dpr));
    const g = sctx.createLinearGradient(-shine.bw, 0, shine.bw, 0);
    g.addColorStop(0, 'rgba(255,246,236,0)');
    g.addColorStop(0.32, 'rgba(222,201,165,0.55)');
    g.addColorStop(0.5, 'rgba(255,252,246,0.95)');
    g.addColorStop(0.68, 'rgba(222,201,165,0.55)');
    g.addColorStop(1, 'rgba(255,246,236,0)');
    shine.grad = g;
  }
  function drawShine(box, k) {
    if (!shine.grad || !(box.w > 0 && box.h > 0)) return;
    const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; // ease in-out
    const lean = box.h * 0.45; // the band leans, so allow for its slant at each end
    const x = -shine.bw - lean + e * (box.w + 2 * (shine.bw + lean));
    sctx.globalCompositeOperation = 'source-over';
    sctx.clearRect(0, 0, box.w, box.h);
    sctx.drawImage(textCanvas, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
    sctx.globalCompositeOperation = 'source-atop';
    sctx.save();
    sctx.translate(x, box.h / 2);
    sctx.rotate(-0.38);
    sctx.fillStyle = shine.grad;
    sctx.fillRect(-shine.bw, -box.h * 2, shine.bw * 2, box.h * 4);
    sctx.restore();
    sctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(shineCanvas, box.x, box.y);
  }

  /* ---------- glitter particles ---------- */
  const particles = [];
  let trailCount = 0;
  const STREAM_COUNT = small ? 3 : 5;
  const streams = Array.from({ length: STREAM_COUNT }, (_, i) => (W * (i + 0.5)) / STREAM_COUNT + (Math.random() - 0.5) * W * 0.08);

  function makeParticle(target) {
    const sx = pick(streams) + (Math.random() - 0.5) * W * 0.05;
    let kind;
    let r;
    let colour;
    if (target && target.upper) {
      // The small ink line: mostly ink flecks with a little champagne.
      kind = Math.random() < 0.85 ? FLAKE : GLITTER;
      r = 0.8 + Math.random() * 0.9;
      colour = kind === FLAKE ? pick(INK) : pick(GOLD);
    } else if (target) {
      // The big champagne word: glitter, the odd sparkle, a few bronze flecks.
      const q = Math.random();
      kind = q < 0.04 ? SPARKLE : q < 0.1 ? FLAKE : GLITTER;
      r = kind === SPARKLE ? 2 + Math.random() * 1.2 : 0.9 + Math.random() * 1.1;
      colour = kind === FLAKE ? '#6E5B4E' : kind === SPARKLE ? pick(PALE) : pick(GOLD);
    } else {
      // Far snow: soft pearl dots with a little drifting champagne.
      const q = Math.random();
      kind = q < 0.86 ? FLAKE : q < 0.97 ? GLITTER : SPARKLE;
      r = kind === FLAKE ? 0.8 + Math.random() * 1.3 : kind === SPARKLE ? 1.8 + Math.random() * 1 : 0.7 + Math.random() * 1;
      colour = kind === FLAKE ? pick(SNOW) : kind === SPARKLE ? pick(PALE) : pick(GOLD);
    }
    return {
      x: sx,
      y: -Math.random() * H * 0.5 - 10 * dpr,
      vx: (Math.random() - 0.5) * 2 * dpr,
      vy: Math.random() * 3.5 * dpr,
      tx: target ? target.x : Math.random() * W,
      ty: target ? target.y : Math.random() * H,
      home: target ? 0.35 + Math.random() * 1.15 : 99, // when the spring towards the letters starts
      fall: (0.2 + Math.random() * 0.4) * dpr,
      r: r * dpr,
      rot: Math.random() * Math.PI,
      c: colour,
      // Far snow runs a little stronger than it did on black: pearl on ivory needs it.
      a: target ? 0.95 : kind === FLAKE ? 0.45 + Math.random() * 0.45 : 0.4 + Math.random() * 0.4,
      target: !!target,
      kind,
      seed: Math.random() * 1000,
      trail: false,
      life: 1,
      decay: 0,
      // Loose snow dots are pearl with a cashmere edge; letter flecks are solid.
      edge: !target && kind === FLAKE,
    };
  }

  function emitTrail(x, y, n) {
    for (let i = 0; i < n && trailCount < TRAIL_MAX; i++) {
      particles.push({
        x: x + (Math.random() - 0.5) * 14 * dpr,
        y: y + (Math.random() - 0.5) * 10 * dpr,
        vx: (-0.4 - Math.random() * 1.1) * dpr,
        vy: Math.random() * 0.4 * dpr,
        tx: 0,
        ty: 0,
        home: 99,
        fall: 0,
        r: (0.6 + Math.random() * 1.1) * dpr,
        rot: Math.random() * Math.PI,
        c: pick(GOLD),
        a: 0.9,
        target: false,
        kind: Math.random() < 0.12 ? SPARKLE : GLITTER,
        seed: Math.random() * 1000,
        trail: true,
        life: 1,
        decay: 0.006 + Math.random() * 0.009, // falls and fades over one to three seconds
      });
      trailCount++;
    }
  }

  // Larger four-point sparkles that flash on the letters once they have settled.
  const letterSparks = [];
  function makeLetterSparks(targets) {
    letterSparks.length = 0;
    const lower = targets.filter((tg) => !tg.upper);
    const pool = lower.length ? lower : targets;
    for (let i = 0; i < LETTER_SPARKS && pool.length; i++) {
      const tg = pick(pool);
      letterSparks.push({
        x: tg.x,
        y: tg.y,
        r: (2.6 + Math.random() * 2.4) * dpr,
        at: SETTLE_AT + Math.random() * 0.9,
        period: 1.5 + Math.random() * 1.9,
        len: 0.45 + Math.random() * 0.2,
      });
    }
  }

  function star(x, y, r) {
    const s = r * 0.32;
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + s, y - s);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x + s, y + s);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - s, y + s);
    ctx.lineTo(x - r, y);
    ctx.lineTo(x - s, y - s);
    ctx.closePath();
    ctx.fill();
  }

  /* ---------- crystal snow: middle layer and near layer ---------- */
  const crystals = [];
  const nears = [];
  function newCrystal(anywhere) {
    const gold = Math.random() < 0.22;
    const depth = 0.45 + Math.random() * 0.55; // 1 is the closest of this layer
    const size = (small ? 9 : 10) + depth * (small ? 12 : 18); // css px
    return {
      x: Math.random() * W,
      y: anywhere ? Math.random() * H : -size * dpr * 1.5,
      s: size * dpr,
      img: (gold ? SPR_GOLD : SPR_PEARL)[(Math.random() * 4) | 0],
      rot: Math.random() * TAU,
      vr: (Math.random() - 0.5) * 0.9, // radians per second
      vy: (14 + depth * 28) * dpr, // px per second
      sway: (6 + Math.random() * 14) * dpr,
      swf: 0.4 + Math.random() * 0.7,
      ph: Math.random() * TAU,
      a: 0.45 + depth * 0.45,
      depth,
    };
  }
  function newNear(anywhere) {
    const size = (small ? 60 : 90) + Math.random() * (small ? 60 : 110);
    return {
      x: Math.random() * W,
      y: anywhere ? Math.random() * H : -size * dpr * 0.6,
      s: size * dpr,
      img: SPR_SOFT[(Math.random() * 4) | 0],
      rot: Math.random() * TAU,
      vr: (Math.random() - 0.5) * 0.35,
      vy: (40 + Math.random() * 40) * dpr,
      sway: (10 + Math.random() * 20) * dpr,
      swf: 0.25 + Math.random() * 0.35,
      ph: Math.random() * TAU,
      a: 0.08 + Math.random() * 0.08,
      depth: 1,
    };
  }
  function makeCrystals() {
    crystals.length = 0;
    nears.length = 0;
    for (let i = 0; i < CRYSTAL_COUNT; i++) crystals.push(newCrystal(true));
    for (let i = 0; i < NEAR_COUNT; i++) nears.push(newNear(true));
  }
  function drawCrystals(list, renew, dt, t, px, fade) {
    for (let i = 0; i < list.length; i++) {
      let c = list[i];
      c.y += c.vy * dt;
      c.rot += c.vr * dt;
      if (c.y - c.s > H) c = list[i] = renew(false);
      const x = c.x + Math.sin(t * c.swf + c.ph) * c.sway + px * c.depth;
      const cr = Math.cos(c.rot);
      const sr = Math.sin(c.rot);
      ctx.setTransform(cr, sr, -sr, cr, x, c.y);
      ctx.globalAlpha = c.a * fade;
      ctx.drawImage(c.img, -c.s / 2, -c.s / 2, c.s, c.s);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /* ---------- the big champagne snowflake behind the wordmark ----------
     A list of segments in unit radius. It grows outward from the centre:
     a segment starts drawing when the growth front reaches its inner end. */
  const med = { segs: [], R: 0, cache: null };
  function buildMedallion() {
    const segs = [];
    const add = (x1, y1, x2, y2) => {
      let d1 = Math.hypot(x1, y1);
      let d2 = Math.hypot(x2, y2);
      if (d2 < d1) {
        [x1, y1, x2, y2] = [x2, y2, x1, y1];
        [d1, d2] = [d2, d1];
      }
      segs.push({ x1, y1, x2, y2, d1, d2 });
    };
    for (let k = 0; k < 6; k++) {
      const a = (k * TAU) / 6 - Math.PI / 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const line = (u1, v1, u2, v2) => add(u1 * ca - v1 * sa, u1 * sa + v1 * ca, u2 * ca - v2 * sa, u2 * sa + v2 * ca);
      line(0.07, 0, 0.93, 0);
      [
        [0.3, 0.2],
        [0.52, 0.28],
        [0.73, 0.19],
        [0.86, 0.08],
      ].forEach(([u, L]) => {
        line(u, 0, u + L * 0.5, L * 0.866);
        line(u, 0, u + L * 0.5, -L * 0.866);
      });
      // Little twigs off the longest branches, parallel to the arm.
      const bu = 0.52 + 0.28 * 0.5 * 0.55;
      const bv = 0.28 * 0.866 * 0.55;
      line(bu, bv, bu + 0.08, bv);
      line(bu, -bv, bu + 0.08, -bv);
      // A diamond at the tip.
      line(0.93, 0, 0.965, 0.036);
      line(0.965, 0.036, 1, 0);
      line(0.93, 0, 0.965, -0.036);
      line(0.965, -0.036, 1, 0);
    }
    const hex = (r, off) => {
      for (let k = 0; k < 6; k++) {
        const a1 = off + (k * TAU) / 6;
        const a2 = off + ((k + 1) * TAU) / 6;
        add(r * Math.cos(a1), r * Math.sin(a1), r * Math.cos(a2), r * Math.sin(a2));
      }
    };
    hex(0.07, -Math.PI / 2);
    hex(0.18, -Math.PI / 2 + Math.PI / 6);
    med.segs = segs;
  }
  const growth = (t) => 1.03 * (1 - Math.pow(1 - clamp01((t - GROW_AT) / GROW), 2.2));
  function strokeMedallion(g, R, rho) {
    g.beginPath();
    for (const s of med.segs) {
      if (rho <= s.d1) continue;
      const k = Math.min(1, (rho - s.d1) / Math.max(s.d2 - s.d1, 0.05));
      g.moveTo(s.x1 * R, s.y1 * R);
      g.lineTo((s.x1 + (s.x2 - s.x1) * k) * R, (s.y1 + (s.y2 - s.y1) * k) * R);
    }
    g.stroke();
  }
  function styleMedallion(g) {
    g.strokeStyle = MED_INK;
    g.lineWidth = Math.max(1, 1.15 * dpr);
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.shadowColor = 'rgba(195,162,110,0.7)';
    g.shadowBlur = 9 * dpr;
  }
  function cacheMedallion() {
    const size = Math.ceil(med.R * 2.1 + 40 * dpr);
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    g.translate(size / 2, size / 2);
    styleMedallion(g);
    strokeMedallion(g, med.R, 2);
    med.cache = c;
  }
  function drawMedallion(t, alpha, px) {
    const rho = growth(t);
    if (alpha < 0.01 || rho <= 0) return;
    const R = med.R;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(centre.cx + px, centre.cy);
    ctx.rotate(t * 0.035);
    if (rho >= 1.02) {
      if (!med.cache) cacheMedallion();
      ctx.drawImage(med.cache, -med.cache.width / 2, -med.cache.height / 2);
    } else {
      styleMedallion(ctx);
      strokeMedallion(ctx, R, rho);
      ctx.shadowBlur = 0;
      // The growth front glints where each line is still being drawn.
      ctx.fillStyle = SPARK_INK;
      const d = 1.3 * dpr;
      for (const s of med.segs) {
        if (rho <= s.d1) continue;
        const k = (rho - s.d1) / Math.max(s.d2 - s.d1, 0.05);
        if (k >= 1) continue;
        ctx.fillRect((s.x1 + (s.x2 - s.x1) * k) * R - d, (s.y1 + (s.y2 - s.y1) * k) * R - d, d * 2, d * 2);
      }
    }
    // A ring of champagne dots closes around it once the arms are out.
    const rk = clamp01((t - (GROW_AT + GROW * 0.75)) / 0.7);
    if (rk > 0) {
      const N = 72;
      const n = Math.floor(rk * N);
      const rr = R * 1.1;
      ctx.fillStyle = MED_INK;
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const a = -Math.PI / 2 + (i / N) * TAU;
        const x = Math.cos(a) * rr;
        const y = Math.sin(a) * rr;
        const r = (i % 6 === 0 ? 1.8 : 1) * dpr;
        ctx.moveTo(x + r, y);
        ctx.arc(x, y, r, 0, TAU);
      }
      ctx.fill();
    }
    ctx.restore();
  }

  /* ---------- fir sprigs and baubles ---------- */
  const branches = [];
  const baubles = [];
  function layoutBranches() {
    branches.length = 0;
    const L = small ? W * 0.34 : Math.min(Math.max(W * 0.22, 250 * dpr), 430 * dpr);
    const left = makeBranch(L, 7);
    const right = makeBranch(L * 0.86, 13);
    branches.push({ ...left, side: 1, x: -L * 0.05, y: -L * 0.035, rot: small ? 0.5 : 0.32, delay: 0.1, sway: 0, px: 0, py: 0 });
    branches.push({ ...right, side: -1, x: W + L * 0.05, y: -L * 0.035, rot: small ? 0.46 : 0.28, delay: 0.25, sway: 0, px: 0, py: 0 });
  }
  function makeBaubles() {
    baubles.length = 0;
    const k = small ? 1 : Math.min(1.25, Math.max(0.85, window.innerWidth / 1440));
    const list = small
      ? [
          { b: 0, s: 0.64, len: 0.12, R: 21, style: 'red', at: 0.55 },
          { b: 1, s: 0.46, len: 0.19, R: 18, style: 'taupe', at: 0.8, engraved: true },
          { b: 1, s: 0.92, len: 0.06, R: 11, style: 'champagne', at: 1.05 },
        ]
      : [
          { b: 0, s: 0.36, len: 0.17, R: 32, style: 'red', at: 0.5 },
          { b: 0, s: 0.8, len: 0.07, R: 20, style: 'champagne', at: 0.8 },
          { b: 1, s: 0.42, len: 0.12, R: 29, style: 'taupe', at: 0.65, engraved: true },
          { b: 1, s: 0.84, len: 0.22, R: 20, style: 'pearl', at: 0.95 },
          { b: 1, s: 0.63, len: 0.31, R: 15, style: 'cashmere', at: 1.2 },
        ];
    list.forEach((d) =>
      baubles.push({
        ...d,
        sprite: makeBauble(d.R * k * dpr, d.style, d.engraved),
        ph: Math.random() * TAU,
        kick: (Math.random() < 0.5 ? -1 : 1) * (0.12 + Math.random() * 0.1),
        glintEvery: 2.4 + Math.random() * 1.6,
      })
    );
  }
  function branchAnchor(b, s) {
    const [lx, ly0] = quadAt(mainStem(b.L), s);
    const ly = ly0 + 2 * dpr;
    const r = b.rot + b.sway;
    const c = Math.cos(r);
    const sn = Math.sin(r);
    return [b.x + b.px + b.side * (lx * c - ly * sn), b.y + b.py + (lx * sn + ly * c)];
  }
  function drawBranches(t, px) {
    for (const b of branches) {
      const e = easeOut(clamp01((t - b.delay) / 1.3));
      b.sway = 0.012 * Math.sin(t * 0.8 + b.side * 1.3);
      b.px = px - b.side * (1 - e) * b.L * 0.18;
      b.py = -(1 - e) * b.L * 0.14;
      if (e <= 0) continue;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = e;
      ctx.translate(b.x + b.px, b.y + b.py);
      ctx.scale(b.side, 1);
      ctx.rotate(b.rot + b.sway);
      ctx.drawImage(b.img, -b.ox, -b.oy);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    for (const bb of baubles) drawBauble(bb, t);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
  }
  function drawBauble(bb, t) {
    const u = t - bb.at;
    if (u <= 0) return;
    const b = branches[bb.b];
    if (!b) return;
    const [ax, ay] = branchAnchor(b, bb.s);
    // It drops on its thread with a small bounce, then swings and settles into a slow sway.
    const len = bb.len * H * (1 - Math.exp(-4.2 * u) * Math.cos(9 * u));
    const theta = bb.kick * Math.exp(-1.0 * u) * Math.sin(3.3 * u) + 0.035 * Math.sin(t * 0.95 + bb.ph);
    const sp = bb.sprite;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.translate(ax, ay);
    ctx.rotate(theta);
    ctx.globalAlpha = Math.min(1, u / 0.18);
    ctx.strokeStyle = 'rgba(159,129,83,0.85)';
    ctx.lineWidth = Math.max(1, 0.9 * dpr);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, len);
    ctx.stroke();
    ctx.drawImage(sp.img, -sp.img.width / 2, len - sp.top);
    // Now and then a glint catches the highlight.
    const gp = (t + bb.ph) % bb.glintEvery;
    if (u > 1.2 && gp < 0.5) {
      const a = Math.sin((Math.PI * gp) / 0.5);
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = a;
      ctx.fillStyle = SPARK_INK;
      star(-sp.R * 0.4, len + sp.cy - sp.R * 0.42, sp.R * 0.34 * (0.6 + 0.4 * a));
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  /* ---------- the landing: flare, sparkle ring and flying champagne snowflakes ---------- */
  const shards = [];
  function burst() {
    for (let i = 0; i < BURST_COUNT; i++) {
      const a = (i / BURST_COUNT) * TAU + Math.random() * 0.25;
      const sp = (6 + Math.random() * 10) * dpr;
      particles.push({
        x: centre.cx,
        y: centre.cy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp * 0.55,
        tx: 0,
        ty: 0,
        home: 99,
        fall: 0,
        r: (1.8 + Math.random() * 2.2) * dpr,
        rot: 0,
        c: pick(PALE),
        a: 1,
        target: false,
        kind: SPARKLE,
        seed: Math.random() * 1000,
        trail: true,
        life: 1,
        decay: 0.012 + Math.random() * 0.012,
      });
      trailCount++;
    }
    const m = small ? 6 : 10;
    for (let i = 0; i < m; i++) {
      const a = (i / m) * TAU + Math.random() * 0.4;
      const sp = (9 + Math.random() * 9) * dpr;
      shards.push({
        x: centre.cx,
        y: centre.cy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp * 0.6,
        rot: Math.random() * TAU,
        vr: (Math.random() - 0.5) * 6,
        s: (16 + Math.random() * 18) * dpr,
        img: pick(SPR_GOLD),
        life: 1,
        span: 1.1 + Math.random() * 0.6,
      });
    }
  }
  function drawShards(dt) {
    if (!shards.length) return;
    let keep = 0;
    for (let i = 0; i < shards.length; i++) {
      const s = shards[i];
      s.life -= dt / s.span;
      if (s.life <= 0) continue;
      s.vx *= 0.94;
      s.vy = s.vy * 0.94 + 0.05 * dpr;
      s.x += s.vx;
      s.y += s.vy;
      s.rot += s.vr * dt;
      shards[keep++] = s;
      const c = Math.cos(s.rot);
      const sn = Math.sin(s.rot);
      ctx.setTransform(c, sn, -sn, c, s.x, s.y);
      ctx.globalAlpha = Math.min(1, s.life * 1.6);
      ctx.drawImage(s.img, -s.s / 2, -s.s / 2, s.s, s.s);
    }
    shards.length = keep;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  function drawFlare(since) {
    if (since < 0 || since > 1.8) return;
    const grow = easeOut(Math.min(1, since / 0.55));
    const a = since < 0.1 ? since / 0.1 : Math.exp(-(since - 0.1) * 2.6);
    const fw = W * 1.4 * grow;
    const fh = Math.max(40 * dpr, centre.bigSize * 0.9);
    const fy = centre.yb - centre.bigSize * 0.33;
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = a * 0.85;
    ctx.drawImage(FLARE, centre.cx - fw / 2, fy - fh / 2, fw, fh);
    const gr = centre.bigSize * 2.4 * (0.6 + 0.4 * grow);
    ctx.globalAlpha = a * 0.3;
    ctx.drawImage(DISC_WHITE, centre.cx - gr, centre.cy - gr, gr * 2, gr * 2);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  /* ---------- load progress: hero images ---------- */
  const waitImgs = Array.from(document.querySelectorAll('[data-intro-wait] img')).filter((img) => img.loading !== 'lazy').slice(0, 3);
  const loaded = () => (waitImgs.length ? waitImgs.filter((img) => img.complete && img.naturalWidth > 0).length / waitImgs.length : 1);

  /* ---------- Santa's sleigh ----------
     A DOM element behind the canvas, flown once with GSAP on a high arc from
     off-screen left to off-screen right. The arc peaks behind the upper half of
     the wordmark so the letters and glitter pass in front of it. The frame loop
     draws a soft glow under it and drops glitter at its runner. */
  const flight = { t: 0, active: false };
  let sw = 0;
  let sh = 0;
  const arcPoint = (t) => {
    const vw = vpW;
    const vh = vpH;
    const x0 = -sw * 1.2;
    const x1 = vw + sw * 0.4;
    const y0 = vh * 0.7;
    const y1 = vh * 0.52;
    const peak = centre.cy / dpr - sh * 0.6; // arc midpoint: body across the top of the letters, reindeer above
    const cyp = (4 * peak - y0 - y1) / 2;
    const cxp = vw * 0.5 - sw * 0.5;
    const u = 1 - t;
    return {
      x: u * u * x0 + 2 * u * t * cxp + t * t * x1,
      y: u * u * y0 + 2 * u * t * cyp + t * t * y1,
    };
  };
  const flightPoint = (t) => {
    const p = arcPoint(t);
    const q = arcPoint(Math.min(1, t + 0.02));
    const wobble = Math.sin(t * Math.PI * 8);
    const slope = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;
    return { x: p.x, y: p.y + wobble * 6, rot: slope * 0.55 + wobble * 2 };
  };
  const flySleigh = () => {
    if (!sleigh) return;
    sw = sleigh.getBoundingClientRect().width || (small ? 192 : 320);
    sh = (sw * 170) / 420;
    flightTween = G.to(flight, {
      t: 1,
      duration: SLEIGH_TIME,
      delay: SLEIGH_DELAY,
      ease: 'power1.inOut',
      onStart() {
        flight.active = true;
        sleigh.style.visibility = 'visible';
      },
      onUpdate() {
        const p = flightPoint(flight.t);
        G.set(sleigh, { x: p.x, y: p.y, rotation: p.rot });
      },
      onComplete() {
        flight.active = false;
        sleigh.style.visibility = 'hidden';
      },
    });
  };

  /* ---------- the frame draws itself ---------- */
  const drawFrame = () => {
    G.to(rulesH, { scaleX: 1, duration: 1.2, ease: 'power3.inOut', delay: 0.05 });
    G.to(rulesV, { scaleY: 1, duration: 1.2, ease: 'power3.inOut', delay: 0.05 });
    G.fromTo(
      corners,
      { opacity: 0, scale: 0.2, rotation: 45 },
      { opacity: 1, scale: 1, rotation: 45, duration: 0.7, ease: 'back.out(3)', stagger: 0.08, delay: 1.0 }
    );
  };

  /* ---------- state machine ---------- */
  let phase = 'pour'; // pour → resolve → exhale → done
  let start = 0;
  let last = 0;
  let maxTime = MIN_TIME;
  let resolveAt = -1;
  let textAlpha = 0;
  let dustAlpha = 1;
  let shown = 0;
  let lastCount = '';
  let lineShown = false;
  const exitFade = { v: 1 };
  const par = { x: 0, y: 0 };
  let centre = { cx: W / 2, cy: H / 2, yb: H / 2, bigSize: 0, splitY: H / 2, box: { x: 0, y: 0, w: 0, h: 0 } };

  const placeLineBottom = () => {
    if (!lineBottom) return;
    lineBottom.style.top = `${Math.round(centre.yb / dpr + Math.max(18, (centre.bigSize * 0.16) / dpr))}px`;
  };

  const toExhale = () => {
    if (phase === 'exhale' || phase === 'done' || done) return;
    phase = 'exhale';
    if (lineTween) lineTween.kill();
    particles.forEach((p) => {
      if (!p.target && !p.trail) return; // far snow keeps falling
      const dx = p.x - centre.cx;
      const dy = p.y - centre.cy;
      const d = Math.hypot(dx, dy) || 1;
      const force = (6 + Math.random() * 14) * dpr;
      p.vx = (dx / d) * force + (Math.random() - 0.5) * 4 * dpr;
      p.vy = (dy / d) * force - Math.random() * 6 * dpr;
    });
    const wipe = { v: 0 };
    const tl = G.timeline({
      onComplete: () => {
        phase = 'done';
        finish();
      },
    });
    tl.to(exitFades, { opacity: 0, duration: 0.35, ease: 'power2.out' }, 0)
      .to(
        { v: textAlpha },
        {
          v: 0,
          duration: 0.55,
          ease: 'power2.in',
          onUpdate() {
            textAlpha = this.targets()[0].v;
          },
        },
        0.05
      )
      .to(exitFade, { v: 0, duration: 0.6, ease: 'power2.in' }, 0)
      .to(canvas, { scale: 1.06, duration: 0.9, ease: 'power3.in' }, 0)
      .to(
        wipe,
        {
          v: 100,
          duration: 0.8,
          ease: 'power4.inOut',
          onStart() {
            if (edge) {
              // Moved with a transform so the sweep never counts as a layout shift.
              edgeH = (edge.offsetParent || el).clientHeight || window.innerHeight;
              edge.style.opacity = '1';
            }
          },
          onUpdate() {
            stage.style.clipPath = `inset(0% 0% ${wipe.v}% 0%)`;
            if (edge) edge.style.transform = `translate3d(0, ${(((100 - wipe.v) / 100) * edgeH).toFixed(2)}px, 0)`;
          },
        },
        0.15
      );
    if (edge) tl.to(edge, { opacity: 0, duration: 0.2 }, 0.8);
    if (typeof window.themeMotionStart === 'function') {
      // Start page reveals while the card is still lifting.
      setTimeout(() => {
        const fn = window.themeMotionStart;
        window.themeMotionStart = null;
        if (typeof fn === 'function') fn();
      }, 500);
    }
  };

  if (skipBtn) skipBtn.addEventListener('click', toExhale);
  window.addEventListener('keydown', onKey);

  /* ---------- sky pass: bokeh, sleigh glow, stars (behind everything) ---------- */
  function drawSky(t, exhaling) {
    for (let i = 0; i < bokeh.length; i++) {
      const b = bokeh[i];
      const x = b.x + Math.sin(t * b.sx + b.ph) * b.ax;
      const y = b.y + Math.cos(t * b.sy + b.ph) * b.ay;
      ctx.globalAlpha = b.a * (0.78 + 0.22 * Math.sin(t * b.bs + b.ph));
      ctx.drawImage(b.img, x - b.r, y - b.r, b.r * 2, b.r * 2);
    }
    if (flight.active && !exhaling) {
      const p = flightPoint(flight.t);
      const gx = (p.x + sw * 0.42) * dpr;
      const gy = (p.y + sh * 0.5) * dpr;
      const rx = sw * 0.62 * dpr;
      const ry = sh * 0.95 * dpr;
      ctx.globalAlpha = 0.2 + 0.05 * Math.sin(t * 5);
      ctx.drawImage(GLOW, gx - rx, gy - ry, rx * 2, ry * 2);
    }
    const rise = Math.min(1, t / 1.4); // stars come up over the first second or so
    ctx.fillStyle = STAR_INK;
    for (let i = 0; i < stars.length; i++) {
      const s = stars[i];
      const tw = 0.5 + 0.5 * Math.sin(t * s.sp + s.ph);
      const on = Math.max(0, Math.min(1, (Math.sin(t * s.slow + s.ph2) + 0.6) * 1.5));
      const alpha = s.a * tw * on * rise;
      if (alpha < 0.02) continue;
      ctx.globalAlpha = alpha;
      if (s.bright) star(s.x, s.y, s.r * 1.6);
      else ctx.fillRect(s.x - s.r, s.y - s.r, s.r * 2, s.r * 2);
    }
    ctx.globalAlpha = 1;
  }

  function frame(now) {
    if (done) return;
    const t = (now - start) / 1000;
    const dt = last ? Math.min(0.05, Math.max(0, (now - last) / 1000)) : 1 / 60;
    last = now;
    const exhaling = phase === 'exhale' || phase === 'done';
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, W, H);

    // The pointer tilts the layers a little; nearer layers move more.
    const tx = pointer.active ? pointer.x / W - 0.5 : 0;
    const ty = pointer.active ? pointer.y / H - 0.5 : 0;
    par.x += (tx - par.x) * 0.06;
    par.y += (ty - par.y) * 0.06;

    drawSky(t, exhaling);

    // Counter: time and image loading both have to agree.
    const timeProg = Math.min(1, t / MIN_TIME);
    const target = t > maxTime ? 1 : Math.min(timeProg, 0.35 + loaded() * 0.65);
    shown += (target - shown) * 0.18;
    if (countEl) {
      const count = String(Math.max(0, Math.min(100, Math.round(shown * 100)))).padStart(3, '0');
      if (count !== lastCount) {
        lastCount = count;
        countEl.textContent = count;
      }
    }

    if (phase === 'pour' && ((t >= MIN_TIME && loaded() === 1) || t >= maxTime)) {
      phase = 'resolve';
      resolveAt = t;
      if (countEl) {
        lastCount = '100';
        countEl.textContent = '100';
      }
      burst();
    }
    const since = resolveAt >= 0 ? t - resolveAt : -1; // seconds since the wordmark landed
    if (phase === 'resolve') {
      textAlpha = smooth(Math.min(1, since / RESOLVE_FADE));
      dustAlpha = 1 - textAlpha * 0.75;
      if (!lineShown && since >= LINE_AT && lineBottom) {
        lineShown = true;
        lineTween = G.to(lineBottom, { opacity: 1, y: 0, duration: LINE_FADE, ease: 'power2.out' });
      }
      if (since >= EXIT_AT) toExhale();
    }

    const fadeIn = Math.min(1, t / 1.2);
    const medA = (since >= 0 ? 0.26 + 0.55 * Math.exp(-since * 2.4) : 0.38) * exitFade.v;
    drawMedallion(t, medA, -par.x * 8 * dpr);
    drawCrystals(crystals, newCrystal, dt, t, par.x * 14 * dpr, fadeIn);
    drawBranches(t, par.x * 6 * dpr);

    if (flight.active && !exhaling) {
      const p = flightPoint(flight.t);
      emitTrail((p.x + sw * 0.1) * dpr, (p.y + sh * 0.72) * dpr, small ? 2 : 3);
    }

    let keep = 0;
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      if (p.trail) {
        p.life -= p.decay;
        if (p.life <= 0) {
          trailCount--;
          continue;
        }
        p.vx *= 0.965;
        p.vy += 0.02 * dpr;
      } else if (exhaling && p.target) {
        p.vx *= 0.97;
        p.vy = p.vy * 0.97 + 0.12 * dpr;
        p.a *= 0.955;
      } else if (p.target && t > p.home) {
        // Spring towards the wordmark; strength ramps up over most of a second.
        const s = Math.min(1, (t - p.home) / 0.8);
        const k = 0.01 + 0.068 * s * s;
        p.vx += (p.tx - p.x) * k;
        p.vy += (p.ty - p.y) * k;
        p.vx *= 0.8;
        p.vy *= 0.8;
      } else if (p.target) {
        // Pouring: gravity plus a lazy swirl.
        const n = Math.sin(p.y * 0.004 + p.seed + t * 2) + Math.cos(p.x * 0.003 - t * 1.4);
        p.vx += n * 0.08 * dpr;
        p.vy += 0.24 * dpr;
      } else {
        // Far snow: a slow fall with a gentle sway, all the way through.
        p.vx += Math.sin(t * 0.9 + p.seed) * 0.02 * dpr;
        p.vx *= 0.97;
        p.vy = Math.min(p.vy + 0.01 * dpr, p.fall);
        if (p.y > H + 20) {
          p.y = -10;
          p.x = Math.random() * W;
        }
      }
      if (pointer.active && !exhaling) {
        const dx = p.x - pointer.x;
        const dy = p.y - pointer.y;
        const d2 = dx * dx + dy * dy;
        const R = 90 * dpr;
        if (d2 < R * R) {
          const d = Math.sqrt(d2) || 1;
          const f = (1 - d / R) * 5 * dpr;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }
      }
      p.x += p.vx;
      p.y += p.vy;
      particles[keep++] = p;

      let alpha = p.a * (p.target ? dustAlpha : 1) * (p.trail ? p.life : 1);
      if (p.kind === SPARKLE) alpha *= 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * 7 + p.seed));
      else if (p.kind === GLITTER) alpha *= 0.72 + 0.28 * Math.sin(t * 6 + p.seed);
      if (alpha < 0.02) continue;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.c;
      if (p.kind === SPARKLE) {
        star(p.x, p.y, p.r);
      } else if (p.kind === FLAKE) {
        if (p.edge) {
          // A soft cashmere halo under the pearl dot keeps it visible on ivory
          // without turning it into a ring.
          ctx.fillStyle = SNOW_EDGE;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * 1.6, 0, TAU);
          ctx.fill();
          ctx.fillStyle = p.c;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, TAU);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.ellipse(p.x, p.y, p.r, p.r * 0.72, p.rot, 0, TAU);
        ctx.fill();
      }
    }
    particles.length = keep;

    if (textAlpha > 0.001) {
      // On ivory the halo multiplies in rather than adding light: a soft
      // champagne shadow around the letters, like foil pressed into card.
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = textAlpha * 0.22;
      ctx.drawImage(glowCanvas, 0, 0, W, H);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = textAlpha;
      ctx.drawImage(textCanvas, 0, 0);
      if (phase === 'resolve' && since >= SHIMMER_AT && since < SHIMMER_AT + SHIMMER) {
        drawShine(centre.box, (since - SHIMMER_AT) / SHIMMER);
      }
    }
    if (!exhaling) drawFlare(since);

    // Sparkles flashing on the settled letters (never during the exhale).
    if (!exhaling && t > SETTLE_AT) {
      ctx.fillStyle = SPARK_INK;
      for (let i = 0; i < letterSparks.length; i++) {
        const s = letterSparks[i];
        const u = t - s.at;
        if (u < 0) continue;
        const ph = u % s.period;
        if (ph > s.len) continue;
        const a = Math.sin((Math.PI * ph) / s.len);
        ctx.globalAlpha = a * 0.95;
        star(s.x, s.y, s.r * (0.6 + 0.4 * a));
      }
    }
    drawShards(dt);
    // Big soft flakes pass in front of everything, as if close to the lens.
    drawCrystals(nears, newNear, dt, t, par.x * 30 * dpr, fadeIn);
    ctx.globalAlpha = 1;
    if (phase !== 'done') raf = requestAnimationFrame(frame);
  }

  const begin = () => {
    if (done) return;
    centre = renderWordmark();
    const targets = sampleTargets(centre.splitY, centre.box);
    if (!targets.length) {
      finish();
      return;
    }
    targets.forEach((tg) => particles.push(makeParticle(tg)));
    for (let i = 0; i < DUST_COUNT; i++) {
      const p = makeParticle(null);
      p.y = Math.random() * H;
      p.vy = p.fall * Math.random();
      particles.push(p);
    }
    makeLetterSparks(targets);
    makeSky();
    buildMedallion();
    med.R = Math.min(W, H) * (small ? 0.42 : 0.37);
    med.cache = null;
    layoutBranches();
    makeBaubles();
    makeCrystals();
    prepShine(centre.box);
    placeLineBottom();
    if (lineBottom) G.set(lineBottom, { opacity: 0, y: 6 });
    G.fromTo(fades, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: 'power3.out', delay: 0.3 });
    drawFrame();
    if (skipBtn) skipBtn.tabIndex = 0;
    start = performance.now();
    // The latest moment the wordmark may land so the whole intro stays under HOLD_MAX.
    maxTime = Math.max(0.6, HOLD_MAX - TAIL - (start - bootAt) / 1000);
    flySleigh();
    window.dispatchEvent(new Event('intro:start'));
    raf = requestAnimationFrame(frame);
  };

  // Wait briefly for Libre Caslon so the wordmark is sampled in the right face.
  const fontReady = document.fonts
    ? Promise.all([document.fonts.load(`400 100px 'Libre Caslon Display'`), document.fonts.load(`400 40px 'Libre Caslon Display'`)])
    : Promise.resolve();
  const startWhenReady = () => whenSized(begin);
  Promise.race([fontReady, new Promise((r) => setTimeout(r, 700))]).then(startWhenReady, startWhenReady);

  // Last resort: whatever else happens, the page is usable after HOLD_MAX.
  setTimeout(() => {
    if (!done) finish();
  }, (HOLD_MAX + 0.4) * 1000);

  window.addEventListener('resize', () => {
    if (done) return;
    const oW = W;
    const oH = H;
    resize();
    if (!(W > 0 && H > 0)) return;
    if (phase === 'pour' || phase === 'resolve') {
      centre = renderWordmark();
      const targets = sampleTargets(centre.splitY, centre.box);
      if (!targets.length) return;
      let j = 0;
      particles.forEach((p) => {
        if (!p.target) return;
        const tg = targets[j++ % targets.length];
        p.tx = tg.x;
        p.ty = tg.y;
      });
      makeLetterSparks(targets);
      makeSky();
      med.R = Math.min(W, H) * (small ? 0.42 : 0.37);
      med.cache = null;
      layoutBranches();
      if (oW > 0 && oH > 0) {
        [...crystals, ...nears].forEach((c) => {
          c.x *= W / oW;
          c.y *= H / oH;
        });
      }
      prepShine(centre.box);
      placeLineBottom();
    }
  });
})();
